RivalIQ.module('models', function (models, App, Backbone, Marionette, $, _, moment, CompareData, companyDisplay) {

    'use strict';

    models.Company = Backbone.Model.extend({
        hasValidToken: function (account, source) {
            const me = this;
            return $.get([
                '/api/account',
                account.get('id'),
                'companies',
                me.get('id'),
                source,
                'access'
            ].join('/'));
        },

        hasChannel: function (source) {
            const me = this;
            return companyDisplay.hasSource(source, me.toJSON());
        },

        getPrimaryRecord: function (source) {
            const me = this;
            return companyDisplay.getSourcePrimaryRecord(source, me.toJSON());
        },

        getDecoratedHandle: function (source) {
            const me = this;
            const fns = {};
            fns[CompareData.sources.TWITTER] = companyDisplay.getTwitterDecoratedHandle;
            fns[CompareData.sources.INSTAGRAM] = companyDisplay.getInstagramDecoratedHandle;
            fns[CompareData.sources.FACEBOOK] = companyDisplay.getFacebookDecoratedHandle;
            fns[CompareData.sources.WEB] = companyDisplay.getCleanSource;
            fns[CompareData.sources.TIKTOK] = companyDisplay.getTiktokDecoratedHandle;

            return (fns[source] ? fns[source](me.toJSON()) : null);
        },

        getLink: function (source) {
            const me = this;
            return companyDisplay.getSourceLink(source, me.toJSON());
        },

        hasNormalizedData: function (concept, presenceId) {

            const me = this;
            const metric = _.find(RivalIQ.metrics.descriptions, {
                id: concept
            });
            const presences = _.get(me.attributes, ['periodSummary', metric.rollupSource], []);
            const presence = _.find(presences, {
                presence: {
                    id: presenceId
                }
            }) || _.head(presences);
            return Boolean(_.get(presence, 'normalized'));
        },

        getValueData: function (concept, value, options) {
            const me = this;
            const compareData = me.collection.compareData;
            value = value || CompareData.valueType.CURRENT;

            const valueData = compareData.getValueData(concept, value, options);
            return _.find(valueData, {
                companyId: me.id
            }).value;
        },

        getTimeSpanData: function (concept, dates, timeAnalysisMode, options) {
            const me = this;
            const compareData = me.collection.compareData;

            dates = dates || me.get('periodSummary').dates;
            timeAnalysisMode = timeAnalysisMode || RivalIQ.enums.timeAnalysisMode.DAILY_VALUES;

            const data = compareData.getTimeSpanData(concept, dates, timeAnalysisMode, me.id, options);
            return _.find(data, {
                companyId: me.id
            }).values;
        },

        getIcon: function (source) {

            const me = this;

            if (!_.isFinite(source)) {

                return _.reduce(CompareData.sources, function (memo, source) {

                    return (memo || me.getIcon(source));
                }, null);
            }

            return companyDisplay.getIconFromMetricSource(source, me.attributes);
        },

        isUnderReview: function () {
            return false;
        },

        hasChangeRequest: function (source) {

            const me = this;
            const changeRequestFlags = me.get('change_request_flags');
            const sources = (_.isFinite(source) ? [source] : CompareData.sources);

            return _.some(sources, function (source) {

                const sourceDetails = CompareData.sourceDetails[source];

                if (!sourceDetails.discoverable || !RivalIQ.state.landscape.channelAccess(source)) {

                    return false;
                }

                const changeRequest = RivalIQ.utils.needsReview.convertBitfieldToObject(changeRequestFlags);

                return changeRequest[sourceDetails.channel];
            });
        },

        stillFinding: function () {

            const me = this;

            const createStatus = me.get('create_status');

            return RivalIQ.utils.createStatus.stillFinding(createStatus);
        },

        createdRecently: function () {

            const me = this;
            const now = moment.utc();
            const createdAt = moment.utc(me.get('created_at'));
            return (now.diff(createdAt, 'days') <= 2);
        },

        stillWaitingForUserEdit: function (source) {

            const me = this;
            const sources = (_.isFinite(source) ? [source] : CompareData.sources);
            const createStatus = me.get('create_status');
            return _.some(sources, function (source) {
                const sourceDetails = CompareData.sourceDetails[source];
                const startedAt = createStatus[`${source}_startedAt`];
                const finishedAt = createStatus[`${source}_finishedAt`];

                if (!sourceDetails.discoverable || !startedAt) {
                    return false;
                }

                // we're still waiting for the user edit if there was a start, but we don't have a finished at yet
                return !finishedAt;
            });
        },

        isUserDeleted: function (source) {

            const me = this;
            const sources = (_.isFinite(source) ? [source] : CompareData.sources);
            const channelDiscoveryData = me.get('channel_discovery_data');
            const user_edit = channelDiscoveryData.user_edit || {};

            return _.some(sources, function (source) {

                const sourceDetails = CompareData.sourceDetails[source];
                const userEdit = user_edit[source];

                if (!sourceDetails.discoverable || !userEdit) {

                    return false;
                }

                return userEdit.removePresence;
            });
        },

        /*
         * Gets the timePeriod for a given source, period and presence
         * This may or may not be the same as the view state timeperiod.
         * Divergence can occur with period rollups or missing data
         */
        getTimePeriod: function (source, period, presenceId) {
            const me = this;

            const presences = me.get('periodSummary')[CompareData.sourceDetails[source].trendKey];
            presenceId = presenceId || _.get(presences, [0, 'presence', 'id']);
            const presence = _.find(presences, function (p) {
                return _.get(p, 'presence.id') === presenceId;
            });

            let timePeriod;
            if (!presence || !_.get(presence, ['dates', 'period1', period])) {
                timePeriod = _.clone(me.get('periodSummary').dates);
            } else {
                const main = presence.dates.period2[period];
                const compare = presence.dates.period1[period];

                timePeriod = {
                    mainPeriodStart: main.startDate,
                    mainPeriodEnd: moment.utc(main.endDate),
                    mainPeriodIntervalDuration: main.duration,
                    mainPeriodIntervalStart: moment.utc(main.intervalStartDate),
                    mainPeriodIntervalEnd: moment.utc(main.intervalEndDate),
                    comparePeriodStart: compare.startDate,
                    comparePeriodEnd: moment.utc(compare.endDate),
                    comparePeriodIntervalStart: moment.utc(compare.intervalStartDate),
                    comparePeriodIntervalEnd: moment.utc(compare.intervalEndDate),
                    comparePeriodIntervalDuration: compare.duration
                };
            }

            RivalIQ.utils.timePeriod.rehydrate(timePeriod);

            return timePeriod;
        },

        /**
         * Returns a boolean indicating if this company matches the company set.
         * @param {string} companySetDescriptor
         * @param {number} [focusCompanyId]
         * @returns {boolean}
         */
        hasCompanySet: function (companySetDescriptor, focusCompanyId) {
            const me = this;

            const companySetNames = me.get('companySetNames');

            if (RivalIQ.state.utils.isOnlyCompanySet(companySetDescriptor)) {
                const focusedCompanyId = RivalIQ.state.utils.getCompanyIdFromSetId(companySetDescriptor);
                return focusedCompanyId === me.id;
            }

            // NOTE the focus company has all tags no matter what
            if (me.id === (focusCompanyId || RivalIQ.state.utils.getFocusedCompanyId())) {
                return true;
            }

            // NOTE if some monster does not specify company set at all then we should
            // default the value to none
            if (_.isUndefined(companySetDescriptor)) {
                companySetDescriptor = RivalIQ.state.companySets.none;
            }

            switch (companySetDescriptor) {
                case RivalIQ.state.companySets.none:
                    return true;
                case RivalIQ.state.companySets.focus:
                    return false;
                default:
                    return _.some(companySetNames, companySetName => {
                        const companySet = RivalIQ.state.companySets.get(companySetName);
                        return companySet &&
                            (companySet.get('companySetId') === companySetDescriptor || companySet.get('name') === companySetDescriptor);
                    });
            }
        },

        getIncompletePostDataDate: function (source, date) {
            const me = this;
            const companyRecord = me.toJSON();

            const isCrossChannel = (source === RivalIQ.enums.sources.CROSS_CHANNEL);
            const sources = (_.isFinite(source) && !isCrossChannel ? [source] : RivalIQ.enums.sources);

            return _.reduce(sources, function (memo, source) {
                const channel = RivalIQ.data.channels.get(source);

                if (channel.get('group') !== 'social') {
                    return memo;
                }

                const trend = me.getPrimaryRecord(source, companyRecord);
                const since = moment.utc(_.get(trend, 'presence.posts_fetched_since'));
                const isMax = (trend && since.isAfter(date) && (!memo || memo < since));
                return (isMax ? since : memo);
            }, null);
        },

        hasIncompletePostData: function (source, date) {
            const me = this;
            return !_.isEmpty(me.getIncompletePostDataDates(source, date));
        }
    });

}, window.moment, RivalIQ.data.CompareData, RivalIQ.utils.companyDisplay);
